import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API_IP } from "../env";
import { getAdminBookings, getAllProviders } from "./adminService";
import "./OverviewScreen.css";

function OverviewScreen({ goToTab }) {
  const token = localStorage.getItem("@uphealth_login_token");
  const [providers, setProviders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingMeta, setBookingMeta] = useState({ totalItems: 0 });
  const [usersMeta, setUsersMeta] = useState({ totalItems: 0 });
  const [requestsCount, setRequestsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [provRes, bookRes, userRes, reqRes] = await Promise.all([
          getAllProviders(token).catch(() => ({ data: [] })),
          getAdminBookings(token, 1, 10, "").catch(() => ({ data: { data: [], pagination: { totalItems: 0 } } })),
          axios.get(`${API_IP}/api/admin/users?page=1&limit=1&search=`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: { data: [], pagination: { totalItems: 0 } } })),
          axios.get(`${API_IP}/api/admin/providers/requests`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: [] })),
        ]);

        if (cancelled) return;
        setProviders(Array.isArray(provRes.data) ? provRes.data : []);
        const bookData = bookRes.data?.data ?? bookRes.data ?? [];
        const bookPag = bookRes.data?.pagination ?? { totalItems: bookData.length };
        setBookings(Array.isArray(bookData) ? bookData : []);
        setBookingMeta(bookPag);
        setUsersMeta(userRes.data?.pagination ?? { totalItems: 0 });
        setRequestsCount(Array.isArray(reqRes.data) ? reqRes.data.length : 0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [token]);

  /* ── Derived metrics ── */
  const verifiedProviders = useMemo(
    () => providers.filter((p) => p.verification === true && p.deactive === false),
    [providers]
  );
  const pendingProviders = useMemo(
    () => providers.filter((p) => p.verification === false && p.deactive === false),
    [providers]
  );
  const unresponsive = useMemo(
    () => bookings.filter((b) => b.paid === false),
    [bookings]
  );

  const serviceBreakdown = useMemo(() => {
    const map = new Map();
    bookings.forEach((b) => {
      const name = b.care_service_name || "Other";
      map.set(name, (map.get(name) || 0) + 1);
    });
    const total = bookings.length || 1;
    return [...map.entries()]
      .map(([name, count]) => ({ name, count, pct: Math.round((count / total) * 100) }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [bookings]);

  /* ── Welcome header date ── */
  const today = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString(undefined, {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    });
  }, []);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  }, []);

  const getBookingStatus = (b) => {
    if (b.paid) return { label: "Paid", cls: "dt-pill--accent" };
    if (b.acceptance === true) return { label: "Confirmed", cls: "dt-pill--success" };
    if (b.acceptance === false) return { label: "Declined", cls: "dt-pill--neutral" };
    return { label: "Pending", cls: "dt-pill--warn" };
  };

  const sparkline = (color, seed = 1) => {
    const points = Array.from({ length: 14 }, (_, i) => {
      const v = 14 - Math.abs(Math.sin((i + seed) * 0.65) * 11);
      return `${(i / 13) * 100},${v}`;
    }).join(" ");
    return (
      <svg className="stat-card__spark" viewBox="0 0 100 26" preserveAspectRatio="none">
        <defs>
          <linearGradient id={`spark-${seed}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.45" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <polygon points={`0,26 ${points} 100,26`} fill={`url(#spark-${seed})`} />
      </svg>
    );
  };

  return (
    <div className="ov-screen">
      {/* ── Welcome header ── */}
      <div className="ov-welcome">
        <div>
          <div className="ov-welcome__eyebrow">
            <i className="bi bi-stars" /> {today}
          </div>
          <h1 className="ov-welcome__title">
            {greeting}, <span>Administrator</span>
          </h1>
          <p className="ov-welcome__sub">
            Here's a snapshot of the UpHealth platform today.
          </p>
        </div>
        <div className="ov-welcome__actions">
          <button className="ov-quick-btn" onClick={() => goToTab && goToTab("booking")}>
            <i className="bi bi-calendar-check" /> Review Bookings
          </button>
          <button className="ov-quick-btn ov-quick-btn--accent" onClick={() => goToTab && goToTab("provider")}>
            <i className="bi bi-shield-check" /> Verify Providers
            {pendingProviders.length > 0 && (
              <span className="ov-quick-btn__badge">{pendingProviders.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── KPI Stat Grid ── */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-card__head">
            <div className="stat-card__label">Total Bookings</div>
            <div className="stat-card__icon stat-card__icon--teal">
              <i className="bi bi-calendar-check" />
            </div>
          </div>
          <div className="stat-card__value">{bookingMeta.totalItems ?? 0}</div>
          <div className="stat-card__delta stat-card__delta--up">
            <i className="bi bi-arrow-up-right" /> {unresponsive.length} unresponsive on this page
          </div>
          {sparkline("#00E5C7", 1)}
        </div>

        <div className="stat-card">
          <div className="stat-card__head">
            <div className="stat-card__label">Active Providers</div>
            <div className="stat-card__icon stat-card__icon--violet">
              <i className="bi bi-person-badge" />
            </div>
          </div>
          <div className="stat-card__value">{verifiedProviders.length}</div>
          <div className="stat-card__delta stat-card__delta--info">
            <i className="bi bi-check-circle" /> {providers.length} total in network
          </div>
          {sparkline("#8B5CF6", 2)}
        </div>

        <div className="stat-card">
          <div className="stat-card__head">
            <div className="stat-card__label">Total Users</div>
            <div className="stat-card__icon stat-card__icon--blue">
              <i className="bi bi-people" />
            </div>
          </div>
          <div className="stat-card__value">{usersMeta.totalItems ?? 0}</div>
          <div className="stat-card__delta stat-card__delta--info">
            <i className="bi bi-graph-up" /> Registered patient accounts
          </div>
          {sparkline("#3B82F6", 3)}
        </div>

        <div className="stat-card">
          <div className="stat-card__head">
            <div className="stat-card__label">Pending Action</div>
            <div className="stat-card__icon stat-card__icon--amber">
              <i className="bi bi-bell" />
            </div>
          </div>
          <div className="stat-card__value">{requestsCount + pendingProviders.length}</div>
          <div className="stat-card__delta stat-card__delta--down">
            <i className="bi bi-exclamation-circle" /> {requestsCount} requests · {pendingProviders.length} verifications
          </div>
          {sparkline("#F59E0B", 4)}
        </div>
      </div>

      {/* ── Two-column layout ── */}
      <div className="ov-grid">
        {/* Recent bookings */}
        <div className="ov-panel">
          <div className="ov-panel__head">
            <div>
              <h3 className="ov-panel__title">Recent Bookings</h3>
              <p className="ov-panel__sub">Latest activity across the platform</p>
            </div>
            <button className="ov-link" onClick={() => goToTab && goToTab("booking")}>
              View all <i className="bi bi-arrow-right" />
            </button>
          </div>
          <div className="ov-list">
            {loading && <div className="dt-loading"><div className="spinner-border" /> Loading…</div>}
            {!loading && bookings.length === 0 && (
              <div className="dt-empty">
                <div className="dt-empty__icon"><i className="bi bi-calendar-x" /></div>
                <h4>No bookings yet</h4>
                <p>New activity will appear here.</p>
              </div>
            )}
            {!loading && bookings.slice(0, 6).map((b) => {
              const s = getBookingStatus(b);
              const initials = `${(b.first_name?.[0] || "")}${(b.last_name?.[0] || "")}`.toUpperCase() || "U";
              return (
                <div key={b.booking_id} className="ov-list__row">
                  <div className="dt-user">
                    <div className="dt-avatar">{initials}</div>
                    <div className="dt-cell-stack">
                      <div className="dt-name">{b.first_name} {b.last_name}</div>
                      <div className="dt-sub">{b.care_service_name} · {b.city}</div>
                    </div>
                  </div>
                  <span className={`dt-pill ${s.cls}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Verification queue */}
        <div className="ov-panel">
          <div className="ov-panel__head">
            <div>
              <h3 className="ov-panel__title">Verification Queue</h3>
              <p className="ov-panel__sub">Providers awaiting approval</p>
            </div>
            <button className="ov-link" onClick={() => goToTab && goToTab("provider")}>
              Review <i className="bi bi-arrow-right" />
            </button>
          </div>
          <div className="ov-list">
            {!loading && pendingProviders.length === 0 && (
              <div className="dt-empty">
                <div className="dt-empty__icon"><i className="bi bi-patch-check" /></div>
                <h4>All caught up</h4>
                <p>No providers waiting for verification.</p>
              </div>
            )}
            {pendingProviders.slice(0, 6).map((p) => {
              const initials = (p.provider_name || "?")
                .split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
              return (
                <div key={p.provider_id} className="ov-list__row">
                  <div className="dt-user">
                    <div className="dt-avatar">
                      {p.img_url ? <img src={p.img_url} alt={p.provider_name} /> : initials}
                    </div>
                    <div className="dt-cell-stack">
                      <div className="dt-name">{p.provider_name || "Unnamed"}</div>
                      <div className="dt-sub">{p.title || "Care provider"} · {p.email}</div>
                    </div>
                  </div>
                  <span className="dt-pill dt-pill--warn">Pending</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Bottom row: services + activity ── */}
      <div className="ov-grid ov-grid--3-2">
        <div className="ov-panel">
          <div className="ov-panel__head">
            <div>
              <h3 className="ov-panel__title">Service Distribution</h3>
              <p className="ov-panel__sub">Most requested care services on this page</p>
            </div>
          </div>
          <div className="ov-bars">
            {serviceBreakdown.length === 0 && (
              <div className="dt-empty"><div className="dt-empty__icon"><i className="bi bi-bar-chart" /></div><h4>No data</h4></div>
            )}
            {serviceBreakdown.map((s, i) => (
              <div className="ov-bar" key={s.name}>
                <div className="ov-bar__head">
                  <span className="ov-bar__name">{s.name}</span>
                  <span className="ov-bar__count">{s.count} <small>·</small> {s.pct}%</span>
                </div>
                <div className="ov-bar__track">
                  <div
                    className={`ov-bar__fill ov-bar__fill--${(i % 5) + 1}`}
                    style={{ width: `${s.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="ov-panel">
          <div className="ov-panel__head">
            <div>
              <h3 className="ov-panel__title">Quick Actions</h3>
              <p className="ov-panel__sub">Jump straight into common tasks</p>
            </div>
          </div>
          <div className="ov-actions">
            <button className="ov-action" onClick={() => goToTab && goToTab("booking")}>
              <div className="ov-action__icon ov-action__icon--teal"><i className="bi bi-clipboard-check" /></div>
              <div>
                <div className="ov-action__title">Assign Providers</div>
                <div className="ov-action__sub">Match unresponsive bookings</div>
              </div>
              <i className="bi bi-chevron-right ov-action__chev" />
            </button>
            <button className="ov-action" onClick={() => goToTab && goToTab("provider")}>
              <div className="ov-action__icon ov-action__icon--violet"><i className="bi bi-shield-check" /></div>
              <div>
                <div className="ov-action__title">Verify Providers</div>
                <div className="ov-action__sub">Review pending applications</div>
              </div>
              <i className="bi bi-chevron-right ov-action__chev" />
            </button>
            <button className="ov-action" onClick={() => goToTab && goToTab("user")}>
              <div className="ov-action__icon ov-action__icon--blue"><i className="bi bi-people" /></div>
              <div>
                <div className="ov-action__title">Manage Users</div>
                <div className="ov-action__sub">Audit patient accounts</div>
              </div>
              <i className="bi bi-chevron-right ov-action__chev" />
            </button>
            <button className="ov-action" onClick={() => goToTab && goToTab("provider")}>
              <div className="ov-action__icon ov-action__icon--amber"><i className="bi bi-bell" /></div>
              <div>
                <div className="ov-action__title">Provider Requests</div>
                <div className="ov-action__sub">{requestsCount} new submissions</div>
              </div>
              <i className="bi bi-chevron-right ov-action__chev" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OverviewScreen;
